import { ElementOf } from "@shared/common/types/typeHelpers";
import {
  ServiceTask_ServiceTaskType,
  ServiceTask_ServiceTaskInfeasibilityReason,
  ServiceTask_ServiceTaskPlannedState,
  ServiceTask as ProtoServiceTask,
} from "@opstel/gen/knop/ui/v1/model/servicetask_pb";
import { createSchema } from "@opstel/query/zod-utils";
import { PlainMessage } from "@bufbuild/protobuf";
import { z } from "zod/v3";

export interface ServiceTask {
  type: ServiceTaskType;
  isPlanned: ServiceTaskPlannedState;
  isFeasible: boolean;
  infeasibilityReasons: InfeasibilityReason[];
}

export const SERVICE_TASK_TYPE = ["CHECKING", "CLEANING"] as const;
export const SERVICE_TASK_PLANNED_STATE = ["PLANNED_UNKNOWN", "YES", "NO", "YES_FIXED"] as const;
export const INFEASIBILITY_REASON = [
  "REASON_UNKNOWN",
  "DURATION_CONFLICT",
  "NO_FACILITY_FOR_SERVICE_TASK",
  "NOT_IN_TIMEWINDOW_OF_FACILITY",
] as const;

export type ServiceTaskType = ElementOf<typeof SERVICE_TASK_TYPE>;
export type ServiceTaskPlannedState = ElementOf<typeof SERVICE_TASK_PLANNED_STATE>;
export type ServiceTaskToggleablePlannedState = "YES" | "NO";
export type InfeasibilityReason = ElementOf<typeof INFEASIBILITY_REASON>;

type ValidServiceTask_ServiceTaskTypes = Exclude<
  ServiceTask_ServiceTaskType,
  ServiceTask_ServiceTaskType.UNKNOWN
>;
function transformServiceTaskTypeEnum(type: ValidServiceTask_ServiceTaskTypes): ServiceTaskType {
  const mapping: Record<ValidServiceTask_ServiceTaskTypes, ServiceTaskType> = {
    [ServiceTask_ServiceTaskType.CHECKING]: "CHECKING",
    [ServiceTask_ServiceTaskType.CLEANING]: "CLEANING",
  };
  return mapping[type];
}

function transformServiceTaskPlannedStateEnum(
  type: ServiceTask_ServiceTaskPlannedState,
): ServiceTaskPlannedState {
  return SERVICE_TASK_PLANNED_STATE[type];
}

function transformInfeasibilityReasonEnum(
  type: ServiceTask_ServiceTaskInfeasibilityReason,
): InfeasibilityReason {
  return INFEASIBILITY_REASON[type];
}

export const serviceTaskSchema = createSchema<ServiceTask, PlainMessage<ProtoServiceTask>>({
  type: z
    .nativeEnum(ServiceTask_ServiceTaskType)
    .refine((type) => type !== ServiceTask_ServiceTaskType.UNKNOWN, {
      message: "ServiceTask type should not be UNKNOWN",
    })
    .transform(transformServiceTaskTypeEnum),
  isPlanned: z
    .nativeEnum(ServiceTask_ServiceTaskPlannedState)
    .transform(transformServiceTaskPlannedStateEnum)
    .refine((type) => type !== "PLANNED_UNKNOWN", {
      message: "ServiceTask isPlanned should not be PLANNED_UNKNOWN",
    }),
  isFeasible: z.boolean(),
  infeasibilityReasons: z.array(
    z
      .nativeEnum(ServiceTask_ServiceTaskInfeasibilityReason)
      .transform(transformInfeasibilityReasonEnum)
      .refine((type) => type !== "REASON_UNKNOWN", {
        message: "ServiceTask infeasibilityReasons should not be REASON_UNKNOWN",
      }),
  ),
});

export const infeasibilityReasonMessages: Record<InfeasibilityReason, string> = {
  REASON_UNKNOWN: "Onbekend",
  DURATION_CONFLICT: "Het treinstel heeft onvoldoende tijd op de knoop",
  NO_FACILITY_FOR_SERVICE_TASK: "Er is geen faciliteit voor deze servicetaak",
  NOT_IN_TIMEWINDOW_OF_FACILITY:
    "In de tijd waarin het treinstel op de knoop is, is er geen faciliteit (lang genoeg) open",
};

export const taskTypeTranslations: Record<ServiceTaskType, string> = {
  CHECKING: "Controle",
  CLEANING: "Reiniging",
};
